// A browser with no storage gets a stand-in before any module reads it
import './utils/storageFallback';
// Import polyfills first
import './polyfills';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { LocaleProvider } from './i18n/LocaleContext';
import LocalizedClerkProvider from './i18n/LocalizedClerkProvider';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';
import { initWebVitals } from './utils/webVitals';
import { installStaleBuildRecovery } from './utils/staleBuild';
import { rememberFirstTouch } from './analytics/attribution';
import { rememberLanding } from './utils/signupSteps';
import { capturePrerenderedPage } from './utils/prerenderedPage';
import { pageLocale } from './i18n/locales';
import { loadLocale } from './i18n/translate';

// A tab from before a deploy reloads onto the new build instead of crashing
installStaleBuildRecovery();

// Where this visit came from, written to this browser and nowhere else. It is
// sent only if the cookie banner is accepted, and deleted if it is declined
// (analytics/attribution.js)
rememberFirstTouch();
rememberLanding();

// Create React Query client
const queryClient = new QueryClient();

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const rootElement = document.getElementById('root');

const AppWrapper = (
  <ErrorBoundary>
    <BrowserRouter>
      {/* The page's language decides Clerk's, so the router and locale come first */}
      <LocaleProvider>
        <LocalizedClerkProvider publishableKey={clerkPubKey}>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </LocalizedClerkProvider>
      </LocaleProvider>
    </BrowserRouter>
  </ErrorBoundary>
);

// Never hydrate. The prerendered HTML (scripts/prerender.mjs) is a Puppeteer
// snapshot of a finished client render, taken after effects ran, so its useId
// values (":r0:" where hydration makes ":R69:") and some text can never match a
// first render: hydrating threw on every page and React re-rendered from scratch
// anyway. A plain root does the same without the errors.
// What stays on screen until then is the snapshot's own page: read it before the
// root is cleared, and the first render shows it while the route's chunk loads.
capturePrerenderedPage();
// The page's language is loaded first (English is built in), so the first render is in
// it; the prerendered page stays on screen meanwhile. If it cannot be fetched, the page
// renders in English rather than not at all
loadLocale(pageLocale(window.location.pathname))
  .catch(() => {})
  .then(() => createRoot(rootElement).render(AppWrapper));

// Signal to react-snap that rendering is complete (for prerendering)
// This helps react-snap know when to capture the HTML
window.snapSaveState = () => {
  // Return any state you want to preserve (optional)
  return {};
};

// No registerRoot here. Renders bundle src/remotion/index.jsx (the worker and
// src/server/render.js both point at it), so registering a composition from the
// web entry rendered nothing and only pulled Remotion — 248 kB — into the first
// load of every page, /pricing and the landing page included. The pages that
// play video import the Player themselves, and still get it.

// Initialize web vitals tracking in production
if (import.meta.env.PROD) {
  initWebVitals();
} 